import React from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { useUpdateMembership } from 'src/features/membership/api/useUpdateMembership';
import MembershipFormModal from 'src/features/membership/components/forms/MembershipFormModal';
import { Membership } from 'src/features/membership/types';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { GET_MEMBERSHIP_RESOURCE } from 'src/features/membership/api/useGetMembership';
import { ICON_MAP } from 'src/utils/constants';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import CustomMembershipFormModal from 'src/features/membership/components/forms/custom/CustomMembershipFormModal';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { useParking } from 'src/providers/ParkingProvider';
import { LIST_MEMBERSHIPS_RESOURCE } from 'src/features/membership/api/useListMemberships';

interface MembershipFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembership: Membership;
    children?: React.ReactNode;
}

function UpdateMembershipFormModal<TData>({
    className,
    semantics,
    children,
    selectedMembership,
}: MembershipFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { category = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateMembership({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [GET_MEMBERSHIP_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_MEMBERSHIPS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            toast({
                title: formatMessage(membershipMessages.updateMembershipSuccess),
                description: formatMessage(membershipMessages.updateMembershipSuccessDescription, {
                    entry: success.name,
                }),
            });
        },
    });

    return category === MEMBERSHIP_TEMPLATE_CATEGORY.CUSTOM ? (
        <CustomMembershipFormModal
            selectedMembership={selectedMembership}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ ...data, membershipId: selectedMembership.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(membershipMessages.editMembershipSubtitle, {
                entry: selectedMembership.name,
            })}
        >
            {children}
        </CustomMembershipFormModal>
    ) : (
        <MembershipFormModal
            selectedMembership={selectedMembership}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ ...data, membershipId: selectedMembership.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(membershipMessages.editMembershipSubtitle, {
                entry: selectedMembership.name,
            })}
        >
            {children}
        </MembershipFormModal>
    );
}

export default UpdateMembershipFormModal;
